/**
 * AuthContext - Manages authentication state and user session
 * 
 * =============================================================================
 * INVARIANTS (see docs/invariants/auth-context-invariants.md)
 * =============================================================================
 * 
 * 5.1 AUTH IS BINARY
 *     - Either a valid session exists, or it does not
 *     - Half-authenticated states must not exist
 * 
 * 5.2 AUTH DOES NOT DECIDE UX
 *     - AuthContext does not navigate
 *     - It exposes state only
 *     - Route guards decide what to render
 * 
 * 5.3 PROFILE CREATION IS ATOMIC
 *     - Profile existence is ensured via RPC (ensure_user_profile)
 *     - Creation and onboarding flags are authoritative
 *     - Partial profiles are treated as incomplete, not valid
 * 
 * =============================================================================
 * DEBUG GUIDE: Look for these prefixes in console:
 * - [AuthContext] - General auth flow
 * - [AuthContext:Session] - Session-related events
 * - [AuthContext:Error] - Error handling
 * - [AuthContext:Profile] - Profile fetch during auth
 * - [AuthContext:SignOut] - Sign out flow
 * =============================================================================
 * 
 * Responsibilities:
 * - User login/logout
 * - Session management
 * - Auth state persistence
 * - Password reset flows
 * - Analytics user identification
 * 
 * Does NOT handle:
 * - User profile data (see UserContext)
 * - Study sessions or statistics
 * - Feature flags (see FeatureFlagsContext)
 */
import { createContext, useContext, useEffect, useState, useRef, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { identifyUser } from '@/lib/analytics';
import { shouldRetryProfileFetch, isAuthError, categorizeAuthError } from '@/utils/authHelpers';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  authError: string | null;
  sessionExpired: boolean;
  onboardingCompleted: boolean | null;
  setOnboardingCompleted: (value: boolean) => void;
  signOut: () => Promise<void>;
  handleAuthError: (error: any, context: string) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState<string | null>(null);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [onboardingCompleted, setOnboardingCompleted] = useState<boolean | null>(null);
  
  // Guard against concurrent profile fetches (prevents race conditions)
  const profileFetchInProgress = useRef(false);

  // ==========================================================================
  // ERROR HANDLER: Centralized auth error handling
  // ==========================================================================
  const handleAuthError = (error: any, context: string) => {
    console.error(`[AuthContext:Error] ❌ Auth error in ${context}:`, error);
    console.log('[AuthContext:Error] Error details:', {
      message: error?.message,
      status: error?.status,
      code: error?.code
    });

    const errorMessage = error?.message || 'An authentication error occurred';
    setAuthError(errorMessage);

    // Categorize errors and provide user-friendly messages
    if (error?.message?.includes('Invalid login credentials')) {
      console.log('[AuthContext:Error] Invalid credentials - NOT logging out');
      // Don't auto-logout for wrong password - just show error
      return;
    } else if (error?.status === 401 || error?.status === 403 || error?.code === 'PGRST301') {
      console.log('[AuthContext:Error] Session expired/invalid - dispatching auth-error event');
      // Session expired or invalid token - dispatch global event
      window.dispatchEvent(new CustomEvent('auth-error', { 
        detail: { status: error.status || 401, message: errorMessage, context } 
      }));

      // Also set local state for immediate UI feedback
      setSessionExpired(true);

      // Auto-logout after 3 seconds - V3: No hard redirect, let auth guards handle it
      console.log('[AuthContext:Error] Will auto-logout in 3 seconds...');
      setTimeout(async () => {
        console.log('[AuthContext:Error] Auto-logout triggered');
        await supabase.auth.signOut();
        setSessionExpired(false);
        // V3: NO window.location.href - auth guards in App.tsx will redirect
      }, 3000);
    }
  };

  // ==========================================================================
  // MAIN AUTH EFFECT: Sets up auth state listener and handles initial session
  // ==========================================================================
  useEffect(() => {
    console.log('[AuthContext] 🚀 Initializing auth state listener...');
    
    // Set up auth state listener FIRST (before checking session)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log('[AuthContext:Session] 📡 Auth state changed:', {
          event,
          hasSession: !!session,
          userId: session?.user?.id,
          email: session?.user?.email
        });

        // =====================================================================
        // PHASE 2: Filter TOKEN_REFRESHED - Don't trigger full profile reload
        // This prevents the "token refresh storm" causing 429 rate limit errors
        // =====================================================================
        if (event === 'TOKEN_REFRESHED') {
          console.log('[AuthContext:Session] 🔄 Token refreshed - updating session only (no profile reload)');
          setSession(session);
          setUser(session?.user ?? null);
          return; // Don't proceed to profile loading
        }

        setSession(session);
        setUser(session?.user ?? null);
        
        // =======================================================================
        // PROFILE FETCH: Load onboarding status when user authenticates
        // This runs ONCE per auth state change, not on every render
        // =======================================================================
        if (session?.user) {
          console.log('[AuthContext:Profile] 👤 User authenticated, loading profile...');
          setLoading(true);  // Reset loading while fetching profile
          
          // Use setTimeout to avoid Supabase deadlock
          setTimeout(async () => {
            // =================================================================
            // PHASE 1: Guard against concurrent profile fetches
            // Prevents race conditions when multiple auth events fire rapidly
            // =================================================================
            if (profileFetchInProgress.current) {
              console.log('[AuthContext:Profile] ⚠️ Profile fetch already in progress, skipping');
              return;
            }
            profileFetchInProgress.current = true;
            
            // =================================================================
            // RETRY LOGIC: Up to 3 attempts with 1s delay for transient failures
            // =================================================================
            const maxRetries = 3;
            let lastError: Error | null = null;
            
            for (let attempt = 1; attempt <= maxRetries; attempt++) {
              try {
                console.log('[AuthContext:Profile] Starting profile query (attempt', attempt, 'of', maxRetries, ') for:', session.user.id);
                
                const { data, error } = await supabase
                  .from('user_profiles')
                  .select('handle, premium, prestige_tier, timezone, study_profile, created_at, onboarding_completed')
                  .eq('id', session.user.id)
                  .maybeSingle();

                console.log('[AuthContext:Profile] Profile query result:', {
                  hasData: !!data,
                  error: error?.message,
                  handle: data?.handle,
                  onboarding_completed: data?.onboarding_completed
                });

                if (error) {
                  // PHASE B: Use extracted isAuthError helper
                  if (isAuthError(error)) {
                    console.error('[AuthContext:Profile] ❌ JWT/Auth error during profile fetch');
                    handleAuthError(error, 'profile fetch in auth state change');
                    return; // Don't retry auth errors
                  }
                  // Other errors - throw to trigger retry
                  throw error;
                }
                
                if (data) {
                  // Profile exists - set onboarding status
                  console.log('[AuthContext:Profile] ✅ Profile found, onboarding_completed:', data.onboarding_completed);
                  setOnboardingCompleted(data.onboarding_completed ?? false);
                  
                  // Identify user for analytics
                  identifyUser(session.user.id, {
                    handle: data.handle,
                    isPremium: data.premium || false,
                    prestige_tier: data.prestige_tier,
                    timezone: data.timezone,
                    study_profile: data.study_profile,
                    signup_date: data.created_at
                  });
                  
                  // Success - exit retry loop
                  break;
                } else {
                  // =================================================================
                  // POST-AUTH GATEWAY: Profile doesn't exist (Google OAuth or trigger failure)
                  // Use ensure_user_profile() RPC - SINGLE SOURCE OF TRUTH for profile creation
                  // This guarantees collision-proof handle generation (UUID-based)
                  // =================================================================
                  console.log('[AuthContext:Profile] ⚠️ No profile found, calling ensure_user_profile RPC...');
                  
                  const { data: ensureResult, error: ensureError } = await supabase.rpc('ensure_user_profile', {
                    p_user_id: session.user.id,
                    p_email: session.user.email
                  });
                  
                  // Type the result for proper access
                  const result = ensureResult as { 
                    success: boolean; 
                    created?: boolean; 
                    profile?: { handle?: string; onboarding_completed?: boolean }; 
                    error?: string;
                    message?: string;
                  } | null;
                  
                  if (ensureError) {
                    console.error('[AuthContext:Profile] ❌ ensure_user_profile RPC error:', ensureError);
                    throw ensureError; // Retry via outer loop
                  }
                  
                  if (!result?.success) {
                    console.error('[AuthContext:Profile] ❌ ensure_user_profile returned failure:', result);
                    setAuthError('Failed to create your profile. Please try again or contact support.');
                    setLoading(false);
                    return;
                  }
                  
                  console.log('[AuthContext:Profile] ✅ Profile ensured via RPC:', 
                    result.created ? 'created new' : 'found existing',
                    'handle:', result.profile?.handle
                  );
                  
                  setOnboardingCompleted(result.profile?.onboarding_completed ?? false);
                  break; // Success - exit retry loop
                }
              } catch (err) {
                lastError = err as Error;
                console.error(`[AuthContext:Profile] ❌ Attempt ${attempt} failed:`, err);
                
                // PHASE B: Use extracted retry decision helper
                if (shouldRetryProfileFetch(attempt, maxRetries, err)) {
                  console.log(`[AuthContext:Profile] ⏳ Waiting 1s before retry ${attempt + 1}...`);
                  await new Promise(resolve => setTimeout(resolve, 1000));
                }
              }
            }
            
            // All retries exhausted
            if (lastError) {
              console.error('[AuthContext:Profile] ❌ All retries failed, last error:', lastError);
              // Don't sign out - let recovery UI handle it
              setOnboardingCompleted(false); // Allow access to onboarding at minimum
            }
            
            // PHASE 4: Always reset guard and loading in finally-equivalent position
            profileFetchInProgress.current = false;
            
            // CRITICAL: Only set loading false AFTER profile status is determined
            console.log('[AuthContext] ✅ Setting loading=false (profile loaded)');
            setLoading(false);
          }, 0);
        } else {
          // No session - reset onboarding status
          console.log('[AuthContext:Session] No session, resetting state');
          setOnboardingCompleted(null);
          setLoading(false);
        }
      }
    );

    // ==========================================================================
    // INITIAL SESSION CHECK: Get existing session on mount + VALIDATE IT
    // Session hardening: validate stored session to prevent stale session issues
    // ==========================================================================
    console.log('[AuthContext] 🔍 Checking for existing session...');
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      console.log('[AuthContext:Session] Initial session check:', {
        hasSession: !!session,
        userId: session?.user?.id
      });
      
      if (session) {
        // SESSION HARDENING: Validate the session by calling getUser()
        // This catches stale/invalid sessions (e.g., after account deletion + re-signup)
        console.log('[AuthContext:Session] Validating session with getUser()...');
        const { data: userData, error: userError } = await supabase.auth.getUser();
        
        if (userError || !userData.user) {
          // Session is invalid/stale - force clean logout
          console.error('[AuthContext:Session] ❌ Session validation failed:', {
            error: userError?.message,
            code: userError?.code,
            status: userError?.status
          });
          
          // Clear local state
          console.log('[AuthContext:Session] Clearing stale session...');
          await supabase.auth.signOut();
          setSession(null);
          setUser(null);
          setOnboardingCompleted(null);
          setLoading(false);
          return;
        }
        
        console.log('[AuthContext:Session] ✅ Session validated successfully');
        setSession(session);
        setUser(session.user);
        // Let onAuthStateChange handle profile loading
        console.log('[AuthContext] Has valid session, waiting for onAuthStateChange...');
      } else {
        console.log('[AuthContext] No initial session, setting loading=false');
        setSession(null);
        setUser(null);
        setLoading(false);
      }
    });

    return () => {
      console.log('[AuthContext] 🔌 Unsubscribing from auth state changes');
      subscription.unsubscribe();
    };
  }, []);

  // ==========================================================================
  // SIGN OUT: Clear all auth state and redirect
  // ==========================================================================
  const signOut = async () => {
    console.log('[AuthContext:SignOut] 🚪 Sign out initiated...');
    try {
      // Attempt to sign out on server
      await supabase.auth.signOut();
      console.log('[AuthContext:SignOut] Server signout successful');
    } catch (error) {
      // Ignore server errors - session may already be invalid
      console.warn('[AuthContext:SignOut] ⚠️ Server signout failed (session may be expired):', error);
    } finally {
      // ALWAYS clear local state regardless of server response
      console.log('[AuthContext:SignOut] Clearing local state...');
      setUser(null);
      setSession(null);
      setOnboardingCompleted(null);
      setSessionExpired(false);
      setAuthError(null);
      
      // V3: NO localStorage theme clear - theme is a DEVICE PREFERENCE, not identity
      // V3: NO window.location.href - let React Router auth guards handle redirect
      // This prevents full page reload which causes theme blinking
      console.log('[AuthContext:SignOut] ✅ State cleared - auth guards will handle redirect');
    }
  };

  // Debug: Log current state on every render
  console.log('[AuthContext] 📊 Current state:', {
    hasUser: !!user,
    userId: user?.id,
    hasSession: !!session,
    loading,
    onboardingCompleted,
    sessionExpired,
    authError
  });

  return (
    <AuthContext.Provider value={{ user, session, loading, authError, sessionExpired, onboardingCompleted, setOnboardingCompleted, signOut, handleAuthError }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};